import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Paintbrush, Home, Grid3x3, Droplet, Triangle, Sparkles, Hammer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { services } from '../mock';

const iconMap = {
  paintbrush: Paintbrush,
  home: Home,
  grid3x3: Grid3x3,
  droplet: Droplet,
  triangle: Triangle,
  sparkles: Sparkles,
  hammer: Hammer
};

export const ServicesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Nos Services
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Une gamme complète de services pour répondre à tous vos besoins en rénovation et construction
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <CardHeader>
                    <div className="w-16 h-16 bg-gray-800 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="text-white" size={28} />
                    </div>
                    <CardTitle className="text-2xl text-gray-900 mb-3">{service.title}</CardTitle>
                    <CardDescription className="text-gray-600 leading-relaxed text-base">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Link to="/contact">
                      <Button variant="outline" className="w-full border-gray-300 text-gray-800 hover:bg-gray-50">
                        Demander un devis
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed Services Info */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Pourquoi choisir nos services ?</h2>
          </div>

          <div className="space-y-12">
            {/* Service 1 */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Paintbrush className="text-gray-700" size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Plâtrerie & Peinture</h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Nos artisans maîtrisent toutes les techniques de plâtrerie et de peinture. Que ce soit pour une rénovation intérieure ou extérieure, nous garantissons des finitions parfaites et durables.
                  </p>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-gray-700 rounded-full mr-3"></span>
                      Préparation et traitement des surfaces
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-gray-700 rounded-full mr-3"></span>
                      Application de peintures de qualité professionnelle
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-gray-700 rounded-full mr-3"></span>
                      Finitions soignées et décoratives
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Service 2 */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Home className="text-gray-700" size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Isolation thermique</h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Améliorez le confort de votre habitat et réalisez des économies d'énergie grâce à nos solutions d'isolation performantes, adaptées à votre bâtiment.
                  </p>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-gray-700 rounded-full mr-3"></span>
                      Isolation extérieure (ITE)
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-gray-700 rounded-full mr-3"></span>
                      Isolation intérieure des murs et combles
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-gray-700 rounded-full mr-3"></span>
                      Conseils personnalisés en efficacité énergétique
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Service 3 */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Droplet className="text-gray-700" size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Rénovation sanitaire</h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Transformez votre salle de bain en un espace moderne et fonctionnel. Nous prenons en charge l'installation complète de vos équipements sanitaires.
                  </p>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-gray-700 rounded-full mr-3"></span>
                      Installation de salles de bain clé en main
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-gray-700 rounded-full mr-3"></span>
                      Remplacement d'équipements sanitaires
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-gray-700 rounded-full mr-3"></span>
                      Plomberie et raccordements
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Un projet en tête ?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Discutons-en ensemble et recevez votre devis personnalisé gratuitement
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-gray-800 hover:bg-gray-700 text-white">
              Obtenir un devis gratuit
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};